import { useEffect, useState } from 'react';

/**
 * Watch for resizes and return the window's size.
 * This triggers a rerender every time the window resizes.
 * For components with expensive renders, this could cause perfomance problems!
 *
 * Only register one actual window event handler at any given time,
 * as it can be used for all hook instances.
 *
 * **TODO:** It would probably be a good idea to allow a timeout
 * to be passed in to debounce the handler.
 */

type ScreenSize = {
  screenWidth: number;
  screenHeight: number;
};

const listeners = new Set<(screenSize: ScreenSize) => void>();
const handler = () => {
  listeners.forEach((listener) =>
    listener({
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    }),
  );
};

export const useScreenSize = (): ScreenSize => {
  const [size, setSize] = useState<ScreenSize>({
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  });

  useEffect(() => {
    listeners.add(setSize);
    if (listeners.size === 1) {
      window.addEventListener('resize', handler);
    }

    return () => {
      listeners.delete(setSize);
      if (listeners.size === 0) {
        window.removeEventListener('resize', handler);
      }
    };
  }, []);

  return size;
};

export default useScreenSize;
